import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { BoxFull, Button, TransactionalSubheader } from "iuvity-ui-components";
import { CampaignCreatorSchema } from "../../validations/schemas";
import { CustomDialog, DialogProps } from "../../components/dialog/CustomDialog";
import { IM_CAMPAIGN_TYPES, REVALIDATE_FORM_PROPS, campaignsCreatorFields } from "../../constants";
import { AppContext } from "../../context";
import { useCampaigns } from "../../hooks/useCampaigns";
import { Header } from "../../components";
import { useNavigate } from "react-router-dom";
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";

interface NewCampaignsProps {
  update?: boolean;
}

export const campaignCreatorDefaultValues = {
  advertiseid: "",
  type: "",
  properties: "",
  status: "INACTIVE",
};

export const NewCampaignPage: React.FC<NewCampaignsProps> = ({ update = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedCampaign },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });
  const {
    getValues,
    reset,
    formState: { isDirty, isValid },
  } = form;

  const { campaignAction } = useCampaigns("DATA");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const formKeysOptions: any = {
    type: IM_CAMPAIGN_TYPES(t)
  };

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionStatus = await campaignAction(update ? "UPDATE" : "CREATE", [
      {
        ...formValues,
        [update ? "updatedby" : "createdby"]: user?.name,
        advertiseid: +formValues.advertiseid
      },
    ]);
    if (actionStatus === "SUCCESS") {
      navigate(-1);
      window.scrollTo(0, 0);
      reset(campaignCreatorDefaultValues);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev: any) => ({
          ...prev,
          visible: true,
          title: t(`IuviMarketing.${update ? "save" : "create"}ModalTitle`),
          content: t(`IuviMarketing.${update ? "save" : "create"}ModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Applications.cancelModalTitle"),
          content: t("Applications.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  useEffect(() => {
    if (update && selectedCampaign) {
      reset({
        id: selectedCampaign.id,
        advertiseid: `${selectedCampaign.advertiseid}`,
        type: selectedCampaign.type,
        properties: selectedCampaign.properties,
        status: selectedCampaign.status
      });
    } else {
      reset(campaignCreatorDefaultValues);
    }
  }, [reset, selectedCampaign, setAppState, update]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("IuviMarketing.editCampaignTitle") : t("IuviMarketing.newCampaignTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
           id={t("IuviMarketing.iuviMarketingTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("IuviMarketing.iuviMarketingTitle"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update ? t("IuviMarketing.editCampaignTitle") : t("IuviMarketing.newCampaignTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <CustomFormComponent
        functionality="IuviMarketing"
        fields={campaignsCreatorFields}
        form={form}
        dropdownOptions={formKeysOptions}
        schemaOfValidation={CampaignCreatorSchema}
        REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
      />

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            label={t("IuviMarketing.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
            className="mr-4"
            variant="p-button-link"
          />
          <Button
            label={
              update
                ? t("IuviMarketing.saveBtnLabel")
                : t("IuviMarketing.createBtnLabel")
            }
            variant="p-button"
            onClick={() => handleModalProps("action")}
            disabled={update ? !(isValid && isDirty) : !isValid}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
